import { h } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import { OrthomolIcon } from '~/components-common/OrthomolIcon';
import { ShippingProgressBar } from './ShippingProgressBar';
import { CartProductList } from './CartProductList';
import { AvailablePaymentMethods } from './AvailablePaymentMethods';
import { useShopware } from '../shopware/ShopwareContext';
import { formatPrice } from '../shopware/shopwareUtils';
import { ShippingTaxHint } from './ShippingTaxHint';
import { TreuepunkteInfo } from '../common/TreuepunkteInfo';
import { pushViewCart, buildGA4ItemFromLineItem } from '../tracking/ecommerceDataLayer';
import { PraemienshopCartHint } from './PraemienshopCartHint';

/* Full cart page that we display on /shop/cart */

export const CartPage = () => {
  const { cart, refreshCart, proceedToCheckout } = useShopware();

  // Fetch cart on mount only if it doesn't exist yet
  useEffect(() => {
    if (!cart) {
      refreshCart();
    }
  }, [cart, refreshCart]);

  // Track view_cart event once when cart is loaded with items
  const hasTrackedViewCart = useRef(false);
  useEffect(() => {
    if (cart?.lineItems?.length && !hasTrackedViewCart.current) {
      hasTrackedViewCart.current = true;

      const ga4Items = cart.lineItems
        .map((lineItem) => buildGA4ItemFromLineItem(lineItem, lineItem.quantity))
        .filter((item): item is NonNullable<typeof item> => item !== null);

      const totalValue = cart.price?.positionPrice || 0;
      pushViewCart(ga4Items, totalValue);
    }
  }, [cart]);

  if (!cart) {
    return <p className="text-center pt-5 pb-3">Lade Warenkorb...</p>;
  }

  if (!cart.lineItems || cart.lineItems.length === 0) {
    return (
      <>
        <PraemienshopCartHint />
        <p className="text-center pt-5 pb-3">Dein Warenkorb ist leer.</p>
      </>
    );
  }

  const totalPrice = cart.price?.positionPrice || 0;

  const deliveryTimeText = cart.deliveries?.[0]?.shippingMethod?.deliveryTime?.name ?? '2 - 3 Tage';

  return (
    <>
      <PraemienshopCartHint />
      <ShippingProgressBar mode="page" />

      <div className="d-flex flex-column flex-xl-row gap-4">
        <div className="me-xl-3 flex-grow-1 position-relative">
          <CartProductList mode="page" showLegend={true} />
        </div>

        {/* total and buttons */}
        <div className="shopping-cart-overview-total d-flex flex-column gap-2 ms-auto">
          <div className="bg-white p-4">
            <h5 className="d-flex text-primary mb-2">
              Gesamtbetrag
              <span id="shopping-cart-total" className="ms-auto me-1">
                {formatPrice(totalPrice)}
              </span>{' '}
              &euro;
            </h5>
            <ShippingTaxHint />
            <button className="btn btn-block btn-primary mt-4 mb-4" onClick={proceedToCheckout}>
              Zur Kasse
            </button>

            <AvailablePaymentMethods />

            <p className="small mb-0">Vorteilscodes kannst Du im letzten Schritt des Bestellvorgangs einlösen.</p>
          </div>

          <div id="product-variant-additional-info" className="product-variant-additional-info">
            <div className="small bg-gray-300 p-3 d-flex flex-column gap-4">
              <div id="product-variant-additional-info-availability" className="mx-1 d-flex gap-3">
                <span className="text-primary">
                  <OrthomolIcon name="delivery-van-24" />
                </span>
                <p className="d-flex flex-column mb-0 text-available">
                  <strong>Sofort verfügbar</strong>
                  Lieferzeit {deliveryTimeText}
                </p>
                {/*
                  <p className="mb-0 text-out-of-stock">
                    Das Produkt ist bald wieder lieferbar.
                  </p>
                */}
              </div>
              <TreuepunkteInfo />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
